import { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActionsBar from '../shared/DialogActionsBar';
import Button from '@mui/material/Button';
import { usePermission, LEVEL } from '../../hooks/usePermission';
import { useGlobalStore } from '../../stores/globalStore';
import { useFeatureVisibilityStore } from '../../stores/featureVisibilityStore';
import { usePurchasingLicensed } from '../../api/licensesApi';
import { useNavContext } from '../../navigation/useNavSections';
import { buildNavSections } from '../../navigation/navConfig';
import './FeatureAccessDialog.css';
import CheckboxField from '../form/CheckboxField';

interface FeatureEntry {
  label: string;
  securityField: string;
  route?: string;
  minLevel?: number;
}

interface FeatureCategory {
  name: string;
  icon: string;
  features: FeatureEntry[];
}

const featureCategories: FeatureCategory[] = [
  {
    name: 'Core',
    icon: 'bi-house',
    features: [
      { label: 'Dashboard', securityField: '', route: '/dashboard' },
      { label: 'Overview', securityField: '', route: '/overview' },
    ],
  },
  {
    name: 'Residents',
    icon: 'bi-people',
    features: [
      { label: 'Residents', securityField: 'dietech_Resident_Level', route: '/people' },
      { label: 'Manage Residents', securityField: 'dietech_Managed_Residents', route: '/people/deleted' },
      { label: 'Food Allergies', securityField: 'dietech_Food_Allergies_Level' },
      { label: 'Meal Pattern', securityField: 'dietech_Meal_Pattern_Level', route: '/meal-patterns' },
      { label: 'Likes/Dislikes', securityField: 'dietech_Likes_Dislikes_Level' },
      { label: 'Medications', securityField: 'dietech_Medications_Level' },
      { label: 'Weight History', securityField: 'dietech_Weight_History_Level', route: '/weight-entry' },
      { label: 'Tube Feed', securityField: 'dietech_Tube_Feed_Level' },
      { label: 'Lab Results', securityField: 'dietech_Lab_Results_Level' },
      { label: 'Diet Fluctuations', securityField: 'dietech_Diet_Fluctuations_Level' },
      { label: 'Special Needs', securityField: 'dietech_Special_Needs_Level' },
      { label: 'Calorie/Protein', securityField: 'dietech_Calorie_Protein_Level' },
      { label: 'Credits', securityField: 'dietech_Credit_Level', route: '/credit-process' },
      { label: 'Intake Study', securityField: 'dietech_Intake_Study_Level' },
      { label: 'Skin/Wound', securityField: 'dietech_Skin_Level' },
    ],
  },
  {
    name: 'Menu Cycle',
    icon: 'bi-calendar3',
    features: [
      { label: 'Menu Cycles', securityField: 'dietech_Menu_Cycle_Level', route: '/menucycle' },
      { label: 'Selective Menu', securityField: 'dietech_Selective_Menu_Level' },
      { label: 'Selective Touch', securityField: 'dietech_Selective_Menu_Touch_Level', route: '/selective-touch' },
      { label: 'Special Events', securityField: 'dietech_Special_Event_Level', route: '/special-events' },
    ],
  },
  {
    name: 'Clinical Nutrition',
    icon: 'bi-heart-pulse',
    features: [
      { label: 'Clinical Nutrition', securityField: 'dietech_Nutritional_Assessment_Level', route: '/clinical-nutrition' },
      { label: 'Assessments', securityField: 'dietech_Nutritional_Assessment_Level', route: '/assessments' },
      { label: 'Plan of Care', securityField: 'dietech_Plan_of_Care_Data_Entry_Level', route: '/plan-of-care' },
      { label: 'Nutrition Compliance', securityField: 'dietech_Pdpm_Level', route: '/pdpm' },
      { label: 'Nutritional Analysis', securityField: 'dietech_Analysis_Level', route: '/nutritional-analysis' },
    ],
  },
  {
    name: 'Food Data',
    icon: 'bi-egg-fried',
    features: [
      { label: 'Recipes', securityField: 'dietech_Recipe_Level', route: '/recipes' },
      { label: 'Items', securityField: 'dietech_Items_Level', route: '/items' },
      { label: 'Tables', securityField: 'dietech_Table_Level', route: '/tables' },
    ],
  },
  {
    name: 'Analysis',
    icon: 'bi-bar-chart',
    features: [
      { label: 'Diet Orders Analysis', securityField: 'dietech_Analysis_Level', route: '/analysis/diet-orders' },
      { label: 'Food Allergies Analysis', securityField: 'dietech_Analysis_Level', route: '/analysis/food-allergies' },
      { label: 'Served Meals Analysis', securityField: 'dietech_Analysis_Level', route: '/analysis/served-meals' },
      { label: 'Demand Forecasting', securityField: 'dietech_Analysis_Level', route: '/analysis/demand-forecasting' },
      { label: 'Cost Analysis', securityField: 'dietech_Analysis_Level', route: '/analysis/cost-menu' },
    ],
  },
  {
    name: 'Reports',
    icon: 'bi-file-earmark-bar-graph',
    features: [
      { label: 'Reports', securityField: 'dietech_Report_Level', route: '/reports' },
    ],
  },
  {
    name: 'Operations',
    icon: 'bi-grid-3x3-gap',
    features: [
      { label: 'Tray Line Setup', securityField: 'dietech_Tray_Line_Level', route: '/tray-line-setup' },
      { label: 'Global Adjustments', securityField: 'dietech_Global_Meal_Pattern_Adjustment_Level', route: '/global-adjustments' },
      { label: 'Dining Rooms', securityField: 'dietech_Dining_Room_Layout_Level', route: '/dining-rooms' },
    ],
  },
  {
    name: 'Kitchen',
    icon: 'bi-tv',
    features: [
      { label: 'Kitchen Display', securityField: 'dietech_Kitchen_Display_Level', route: '/kitchen' },
    ],
  },
  {
    name: 'Purchasing',
    icon: 'bi-bag',
    features: [
      { label: 'Purchasing Hub', securityField: 'pur_Level', route: '/ordering' },
      { label: 'Purchase Orders', securityField: 'pUR_SO_Level', route: '/ordering/orders' },
      { label: 'Order Guides', securityField: 'pUR_SO_Level', route: '/ordering/order-guides' },
      { label: 'Receiving', securityField: 'pUR_Inv_Level', route: '/ordering/receiving' },
      { label: 'Vendor Invoices', securityField: 'pUR_Inv_Level', route: '/ordering/invoices' },
      { label: 'Inventory', securityField: 'pUR_Inv_Level', route: '/ordering/inventory' },
      { label: 'Purchasing Setup', securityField: 'pUR_Tables_Level', route: '/ordering/setup' },
    ],
  },
  {
    name: 'Administration',
    icon: 'bi-shield-lock',
    features: [
      { label: 'User Management', securityField: 'user_Level', minLevel: LEVEL.USER_MGMT, route: '/users' },
      { label: 'Logged-In Users', securityField: 'user_Users_Logged_In_Level', route: '/users/logged-in' },
      { label: 'Roles', securityField: 'roles_Level', route: '/roles' },
      { label: 'System Log', securityField: 'dietech_Interaction_Logging_Level', route: '/system-log' },
    ],
  },
];

function getLevelLabel(level: number): { text: string; color: string } {
  switch (level) {
    case 0: return { text: 'No Access', color: 'var(--text-muted)' };
    case 1: return { text: 'View Only', color: '#2196f3' };
    case 2: return { text: 'Add/Edit', color: '#4caf50' };
    case 3: return { text: 'Full Access', color: '#1b5e20' };
    case 6: return { text: 'Full Access', color: '#1b5e20' };
    default: return { text: `Level ${level}`, color: '#ff9800' };
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function FeatureAccessDialog({ open, onClose }: Props) {
  const { getLevel } = usePermission();
  const roleName = useGlobalStore((s) => s.userRole?.name);
  const { isVisible, toggleRoute, showAll, hideAll } = useFeatureVisibilityStore();
  // Purchasing is offered only to facilities licensed for it, so it has no checkbox otherwise.
  const purchasingLicensed = usePurchasingLicensed();

  // Every section and page the role allows, hides NOT applied: this is where a hidden one comes back.
  // Same list as the sidebar (navigation/navConfig.ts), so the checkboxes match what it shows.
  const navCtx = useNavContext();
  const sections = useMemo(() => buildNavSections(navCtx, { applyHides: false }), [navCtx]);
  // A page listed in two sections (Selective Touch: Kitchen and Residents) has one checkbox in each,
  // both for the same key, so it is counted once, as shown when either of its sections is.
  const hideableKeys = new Map<string, string[]>();
  for (const s of sections) {
    for (const p of s.pages) {
      if (!p.alwaysShown) hideableKeys.set(p.key, [...(hideableKeys.get(p.key) ?? []), s.hideKey]);
    }
  }
  const shownCount = [...hideableKeys].filter(([key, sectionKeys]) => isVisible(key) && sectionKeys.some(isVisible)).length;

  // Detailed feature access list
  const categoriesWithAccess = featureCategories
    .filter((cat) => cat.name !== 'Purchasing' || purchasingLicensed)
    .map((cat) => {
    const featuresWithLevel = cat.features.map((f) => {
      const level = f.securityField ? getLevel(f.securityField) : LEVEL.FULL;
      const minLevel = f.minLevel ?? LEVEL.VIEW;
      const hasAccess = level >= minLevel;
      return { ...f, level, hasAccess };
    });
    return { ...cat, features: featuresWithLevel };
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: 'var(--font-size-large)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <i className="bi bi-grid-3x3-gap" style={{ fontSize: 'var(--icon-size)' }} />
        Feature Access
      </DialogTitle>
      <DialogContent>
        {/* Role summary */}
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--surface-bg)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--font-size-small)', color: 'var(--text-secondary)' }}>
              Role: <strong style={{ color: 'var(--text-primary)' }}>{roleName || 'Default'}</strong>
            </span>
            <span style={{ fontSize: 'var(--font-size-small)', color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{shownCount}</strong> of {hideableKeys.size} pages shown in the menu
            </span>
          </div>
        </div>

        {/* Sidebar visibility controls */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 8,
          }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--font-size-base)', color: 'var(--text-primary)' }}>
              Menu Visibility
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => showAll()}
                sx={{ textTransform: 'none', fontSize: '12px', padding: '2px 8px' }}
              >
                Show All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => hideAll(sections.map((sec) => sec.hideKey))}
                sx={{ textTransform: 'none', fontSize: '12px', padding: '2px 8px' }}
              >
                Hide All
              </Button>
            </div>
          </div>
          <div className="fad-sections">
            {sections.map((section) => {
              const sectionShown = isVisible(section.hideKey);
              return (
                <fieldset key={section.id} className="fad-section">
                  <legend>
                    <CheckboxField
                      className="fad-check fad-check--section"
                      label={section.label}
                      value={sectionShown}
                      onValueChanged={() => toggleRoute(section.hideKey)}
                    />
                  </legend>
                  {section.pages.filter((p) => !p.alwaysShown).map((page) => (
                    <CheckboxField
                      key={page.key}
                      className="fad-check"
                      label={page.label}
                      value={sectionShown && isVisible(page.key)}
                      disabled={!sectionShown}
                      onValueChanged={() => toggleRoute(page.key)}
                    />
                  ))}
                </fieldset>
              );
            })}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: 4, paddingLeft: 4 }}>
            Controls which sections and pages appear in the menu. Security access is not affected.
          </div>
        </div>

        {/* Detailed feature access list */}
        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--font-size-base)', color: 'var(--text-primary)' }}>
            All Feature Access Levels
          </span>
          <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 8 }}>
            {categoriesWithAccess.map((cat) => {
              const accessCount = cat.features.filter((f) => f.hasAccess).length;
              if (accessCount === 0) return null;
              return (
                <div key={cat.name} style={{ marginBottom: 12 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '2px 0', marginBottom: 4,
                    borderBottom: '1px solid var(--border-color)',
                  }}>
                    <i className={`bi ${cat.icon}`} style={{ fontSize: 'var(--font-size-small)', color: 'var(--primary-color)' }} />
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-small)', color: 'var(--text-primary)' }}>
                      {cat.name}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                      {accessCount}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
                    {cat.features
                      .filter((f) => f.hasAccess)
                      .map((f) => {
                        const levelInfo = getLevelLabel(f.level);
                        return (
                          <div
                            key={f.label}
                            style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '2px 6px', borderRadius: 4,
                              fontSize: '12px',
                            }}
                          >
                            <span style={{ color: 'var(--text-primary)' }}>{f.label}</span>
                            <span style={{
                              color: levelInfo.color,
                              fontSize: '11px',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              marginLeft: 8,
                            }}>
                              {f.securityField ? levelInfo.text : ''}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
      <DialogActionsBar onCancel={onClose} cancelLabel="Close" />
    </Dialog>
  );
}
